import React, { useEffect, useState } from "react";
import {
  AreaChart,
  BarChart,
  Card,
  DonutChart,
  Legend,
  Metric,
  Text,
  Title,
} from "@tremor/react";
import {
  ComposableMap,
  Geographies,
  Geography,
  ZoomableGroup,
} from "react-simple-maps";
import axios from "axios";
import { base_url } from "../../config";
import countryData from "./countryData.json";
import {
  Factory,
  Presentation,
  Users,
  GraduationCap,
  Plane,
  FileText,
  Ticket,
  Glasses,
  Wine,
  PartyPopper,
} from "lucide-react";

export function getNumericCountryCode(alpha2) {
  const country = countryData.find(
    (entry) => entry["alpha-2"].toUpperCase() === alpha2.toUpperCase()
  );
  return country ? country["country-code"] : null;
}

const sampleData = {
  completed: 0,
  students: 0,
  industryDay: 0,
  exhibitors: 0,
  visaLettersSent: 0,
  totalPapers: 0,
  registrationCodesUsed: 0,
  byType: [],
  byCategory: [],
  byPhase: [],
  socialEventConfirmations: {},
  dailyRegistrations: [],
  byCountry: [],
};

const dateFormatter = (s) => {
  const d = new Date(s);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

const numberFormatter = (n) => Intl.NumberFormat("en-US").format(n).toString();

// Compact KPI card — left icon tile, right label + value
const StatTile = ({ title, value, icon, color = "blue", loading }) => {
  // Tremor tone helpers via dark/Tailwind utility strings
  const tones = {
    blue:    { bg: "bg-blue-500/15 dark:bg-blue-500/20",       text: "text-blue-500 dark:text-blue-400"  },
    cyan:    { bg: "bg-cyan-500/15 dark:bg-cyan-500/20",       text: "text-cyan-500 dark:text-cyan-400"  },
    amber:   { bg: "bg-amber-500/15 dark:bg-amber-500/20",     text: "text-amber-500 dark:text-amber-400" },
    violet:  { bg: "bg-violet-500/15 dark:bg-violet-500/20",   text: "text-violet-500 dark:text-violet-400" },
    rose:    { bg: "bg-rose-500/15 dark:bg-rose-500/20",       text: "text-rose-500 dark:text-rose-400" },
    emerald: { bg: "bg-emerald-500/15 dark:bg-emerald-500/20", text: "text-emerald-500 dark:text-emerald-400" },
    orange:  { bg: "bg-orange-500/15 dark:bg-orange-500/20",   text: "text-orange-500 dark:text-orange-400" },
  };
  const t = tones[color] ?? tones.blue;
  return (
    <Card className="!p-3 !rounded-2xl dark:!bg-slate-800/50 dark:!border-slate-700/40">
      <div className="flex items-center gap-3">
        <div
          className={`h-10 w-10 rounded-xl flex items-center justify-center flex-shrink-0 ${t.bg} ${t.text}`}
        >
          {icon}
        </div>
        <div className="min-w-0 flex-1">
          <Text className="!text-[10px] !uppercase !tracking-wider !text-slate-500 dark:!text-slate-400 truncate">
            {title}
          </Text>
          {loading ? (
            <div className="h-6 mt-1 flex items-center">
              <span className="inline-block h-2 w-12 rounded bg-slate-300 dark:bg-slate-700 animate-pulse" />
            </div>
          ) : (
            <Metric className="!text-xl !leading-tight !text-slate-900 dark:!text-slate-100">
              {numberFormatter(value ?? 0)}
            </Metric>
          )}
        </div>
      </div>
    </Card>
  );
};

export default function Dashboard() {
  const [registrationData, setRegistrationData] = useState(sampleData);
  const [loading, setLoading] = useState(true);
  const [countryMap, setCountryMap] = useState({});
  const [tooltipContent, setTooltipContent] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${base_url}/api/admin/dashboard`);
        setRegistrationData(response.data);
        const dict = {};
        (response.data.byCountry || []).forEach((c) => {
          const code = getNumericCountryCode(c.id);
          if (code) dict[code] = c.value;
        });
        setCountryMap(dict);
      } catch (error) {
        console.error("Error fetching dashboard:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const byCountry = registrationData.byCountry || [];

  const getColorScale = (value) => {
    if (!byCountry.length) return "#1e293b";
    const max = Math.max(...byCountry.map((d) => d.value), 1);
    const min = Math.min(...byCountry.map((d) => d.value), 0);
    const t = max === min ? 1 : (value - min) / (max - min);
    const r = Math.round(59 + t * (34 - 59));
    const g = Math.round(130 + t * (211 - 130));
    const b = Math.round(246 + t * (238 - 246));
    return `rgb(${r}, ${g}, ${b})`;
  };

  const social = registrationData.socialEventConfirmations || {};

  const socialItems = [
    {
      key: "reception-Yes",
      label: "Welcome Reception",
      icon: <Wine className="h-4 w-4" />,
      iconClass: "bg-cyan-500/15 text-cyan-300",
    },
    {
      key: "student_reception-Yes",
      label: "Student Reception",
      icon: <PartyPopper className="h-4 w-4" />,
      iconClass: "bg-violet-500/15 text-violet-300",
    },
    {
      key: "gala_dinner-Yes",
      label: "Banquet",
      icon: <Glasses className="h-4 w-4" />,
      iconClass: "bg-amber-500/15 text-amber-300",
    },
  ];

  return (
    <div className="dark min-h-screen bg-slate-950 text-slate-100">
      <div className="mx-auto max-w-[1400px] px-4 py-5 space-y-5">
        <header className="flex items-baseline justify-between">
          <Title className="!text-slate-100">Dashboard</Title>
          <Text className="!text-slate-400">
            IJCAI-ECAI 2026 registration overview
          </Text>
        </header>

        {/* KPI row */}
        <section className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-3">
          <StatTile title="Completed"      value={registrationData.completed}             icon={<Users          className="h-5 w-5" />} color="blue"    loading={loading} />
          <StatTile title="Students"       value={registrationData.students}              icon={<GraduationCap  className="h-5 w-5" />} color="cyan"    loading={loading} />
          <StatTile title="Industry day"   value={registrationData.industryDay}           icon={<Factory        className="h-5 w-5" />} color="amber"   loading={loading} />
          <StatTile title="Exhibitors"     value={registrationData.exhibitors}            icon={<Presentation   className="h-5 w-5" />} color="violet"  loading={loading} />
          <StatTile title="Visa letters"   value={registrationData.visaLettersSent}       icon={<Plane          className="h-5 w-5" />} color="rose"    loading={loading} />
          <StatTile title="Total papers"   value={registrationData.totalPapers}           icon={<FileText       className="h-5 w-5" />} color="emerald" loading={loading} />
          <StatTile title="Reg. codes used" value={registrationData.registrationCodesUsed} icon={<Ticket        className="h-5 w-5" />} color="orange"  loading={loading} />
        </section>

        {/* Social-event mini cards */}
        <Card className="!rounded-2xl dark:!bg-slate-800/50 dark:!border-slate-700/40">
          <Title className="!text-[11px] !uppercase !tracking-wider !text-slate-300">
            Social events confirmations
          </Title>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
            {socialItems.map((s) => (
              <div
                key={s.key}
                className="flex items-center gap-3 rounded-xl border border-slate-700/40 bg-slate-900/40 px-3 py-2"
              >
                <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${s.iconClass}`}>
                  {s.icon}
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-slate-400">{s.label}</p>
                  <p className="text-base font-semibold text-slate-100">
                    {social[s.key] ?? 0}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Three breakdown charts */}
        <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card className="!rounded-2xl dark:!bg-slate-800/50 dark:!border-slate-700/40">
            <Title className="!text-[11px] !uppercase !tracking-wider !text-slate-300">
              Registration by type
            </Title>
            <BarChart
              className="mt-3 h-56"
              data={registrationData.byType || []}
              index="name"
              categories={["value"]}
              colors={["blue"]}
              valueFormatter={numberFormatter}
              showLegend={false}
              yAxisWidth={36}
            />
          </Card>

          <Card className="!rounded-2xl dark:!bg-slate-800/50 dark:!border-slate-700/40">
            <Title className="!text-[11px] !uppercase !tracking-wider !text-slate-300">
              Registration by category
            </Title>
            <BarChart
              className="mt-3 h-56"
              data={registrationData.byCategory || []}
              index="name"
              categories={["value"]}
              colors={["cyan"]}
              valueFormatter={numberFormatter}
              showLegend={false}
              yAxisWidth={36}
            />
          </Card>

          <Card className="!rounded-2xl dark:!bg-slate-800/50 dark:!border-slate-700/40">
            <Title className="!text-[11px] !uppercase !tracking-wider !text-slate-300">
              Registration by phase
            </Title>
            <DonutChart
              className="mt-3 h-56"
              data={registrationData.byPhase || []}
              category="value"
              index="name"
              colors={["blue", "cyan", "amber", "violet", "rose", "emerald"]}
              valueFormatter={numberFormatter}
            />
            <Legend
              className="mt-2"
              categories={(registrationData.byPhase || []).map((p) => p.name)}
              colors={["blue", "cyan", "amber", "violet", "rose", "emerald"]}
            />
          </Card>
        </section>

        {/* Daily registrations area chart */}
        <Card className="!rounded-2xl dark:!bg-slate-800/50 dark:!border-slate-700/40">
          <Title className="!text-[11px] !uppercase !tracking-wider !text-slate-300">
            Daily registrations
          </Title>
          <AreaChart
            className="mt-3 h-60"
            data={(registrationData.dailyRegistrations || []).map((d) => ({
              date: dateFormatter(d.date),
              Registrations: d.count,
            }))}
            index="date"
            categories={["Registrations"]}
            colors={["blue"]}
            valueFormatter={numberFormatter}
            showLegend={false}
            yAxisWidth={36}
            curveType="monotone"
          />
        </Card>

        {/* Choropleth (kept on react-simple-maps) */}
        <Card className="relative !rounded-2xl dark:!bg-slate-800/50 dark:!border-slate-700/40">
          <Title className="!text-[11px] !uppercase !tracking-wider !text-slate-300">
            Registrations by country
          </Title>
          {tooltipContent && (
            <div className="absolute z-10 top-10 left-6 rounded-md border border-slate-600 bg-slate-900/95 px-2.5 py-1.5 text-xs text-slate-200 shadow-lg pointer-events-none">
              {tooltipContent}
            </div>
          )}
          <div className="mt-3 h-[420px] rounded-xl overflow-hidden border border-slate-700/40 bg-slate-950">
            <ComposableMap
              projectionConfig={{ scale: 160 }}
              width={800}
              height={420}
              style={{ width: "100%", height: "100%", background: "#0b1220" }}
            >
              <ZoomableGroup>
                <Geographies geography="https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json">
                  {({ geographies }) =>
                    geographies.map((geo) => {
                      const v = countryMap[geo.id];
                      return (
                        <Geography
                          key={geo.rsmKey}
                          geography={geo}
                          onMouseEnter={() =>
                            setTooltipContent(
                              `${geo.properties.name}: ${v !== undefined ? v : 0}`
                            )
                          }
                          onMouseLeave={() => setTooltipContent("")}
                          fill={v ? getColorScale(v) : "#1f2937"}
                          stroke="#0f172a"
                          strokeWidth={0.4}
                          style={{
                            default: { outline: "none", opacity: 1 },
                            hover: { outline: "none", opacity: 0.85 },
                            pressed: { outline: "none" },
                          }}
                        />
                      );
                    })
                  }
                </Geographies>
              </ZoomableGroup>
            </ComposableMap>
          </div>
        </Card>
      </div>
    </div>
  );
}
